import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import { FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import VaccinesOutlinedIcon from '@mui/icons-material/VaccinesOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ModuleCard from '../moduleCard/ModuleCard';
import DateChip from '../moduleCard/DateChip';
import CustomButton from '../../../../components/buttons/CustomButton';
import { variantFromGender } from '../../../../components/bodySilhouette/bodySilhouetteFigures';
import { DataProviderInstance } from '../../../../api/DataProvider';
import ApplicationFigure from './ApplicationFigure';
import ApplicationMarkList from './ApplicationMarkList';
import ApplicationMarkPopup from '../../../../popup/ApplicationMarkPopup/ApplicationMarkPopup';
import ApplicationProductsPopup from '../../../../popup/ApplicationProductsPopup/ApplicationProductsPopup';
import ApplicationPhotoPopup from '../../../../popup/ApplicationPhotoPopup/ApplicationPhotoPopup';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import { buildApplicationMapJob } from '../../modules/reportSections';
import ApplicationMapReportTemplate from '../../../../components/documents-templates/ApplicationMapReportTemplate';
import { APPLICATION_VIEWS, APPLICATION_VIEW_OPTIONS, sameBackground } from './applicationFigures';
import { resolveProducts } from './applicationProducts';
import {
  MARK_KINDS,
  createPoint,
  createStroke,
  findOwnRecord,
  formatAmount,
  formatDate,
  formatQuantity,
  markSummary,
  sortByDateDesc,
  toFigureMarks,
  toQuantity,
  totalsByProduct,
} from './applicationMarks';

const SAVE_DELAY_MS = 700;

const SAVE_LABELS = {
  pending: 'Alterações pendentes',
  saving: 'Salvando...',
  error: 'Não foi possível salvar. A próxima alteração tenta de novo.',
};

const EMPTY_FIELDS = { marks: [], backgrounds: {}, batch: '', notes: '' };

// Fundos por vista ({ face: { attachmentId, crop } }); sem entrada vale o desenho
const backgroundsOf = (record) => (
  record.backgrounds && typeof record.backgrounds === 'object' && !Array.isArray(record.backgrounds)
    ? record.backgrounds
    : {}
);

const fieldsOf = (record) => (record
  ? { marks: record.marks || [], backgrounds: backgroundsOf(record), batch: record.batch || '', notes: record.notes || '' }
  : EMPTY_FIELDS);

const countLabel = (count) => `${count} ${count === 1 ? 'marcação' : 'marcações'}`;

/**
 * Módulo "Mapa de aplicação": pontos e traços com direção sobre o rosto ou o
 * corpo, cada um com produto e quantidade (toxina, preenchedor, fios...), um
 * mapa por atendimento, salvo a cada alteração. Os totais por produto somam
 * as marcações da sessão.
 *
 * `data`: mapas do paciente (todos os atendimentos).
 * `products`: produtos da clínica (vazio usa a lista padrão).
 * `gender`: sexo do cadastro, escolhe a figura.
 * `enabled`: false na aba Saúde do paciente (só leitura, com seleção de data).
 * `currentDate`: data do atendimento, sugerida ao enviar uma foto de fundo.
 * `patientName`, `professionalId`, `companyConfig`: identificação no PDF.
 */
const ApplicationMap = ({
  data,
  products: companyProducts,
  patientId,
  appointmentId,
  gender,
  enabled,
  collapsible = false,
  currentDate,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const { SetPatientApplicationMap, GetPatientAttachmentFile } = DataProviderInstance();
  const records = useMemo(() => sortByDateDesc(Array.isArray(data) ? data : []), [data]);
  const ownRecord = enabled ? findOwnRecord(records, appointmentId) : null;
  const previousRecords = enabled ? records.filter((record) => record !== ownRecord) : [];

  const [fields, setFields] = useState(fieldsOf(ownRecord));
  const [products, setProducts] = useState(() => resolveProducts(companyProducts));
  const [view, setView] = useState(APPLICATION_VIEWS.FACE);
  const [activeProductIndex, setActiveProductIndex] = useState(0);
  const [nextQuantity, setNextQuantity] = useState(() => formatQuantity(resolveProducts(companyProducts)[0].defaultQuantity));
  const [showQuantities, setShowQuantities] = useState(true);
  const [showPrevious, setShowPrevious] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [productsOpen, setProductsOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);

  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const saveRef = useRef(null);

  // Resincroniza com o servidor só quando não há alteração a caminho
  useEffect(() => {
    if (!enabled || pendingRef.current || busyRef.current) return;
    setFields(fieldsOf(ownRecord));
  }, [ownRecord, enabled]);

  useEffect(() => {
    setProducts(resolveProducts(companyProducts));
  }, [companyProducts]);

  useEffect(() => {
    if (!enabled) setSelectedRecordId(records.length > 0 ? String(records[0].id) : null);
  }, [records, enabled]);

  function flush() {
    const payload = pendingRef.current;
    if (!payload || busyRef.current) return;
    pendingRef.current = null;
    busyRef.current = true;
    setSaveState('saving');
    SetPatientApplicationMap(patientId, appointmentId, payload, (response) => {
      busyRef.current = false;
      if (response && response.status === 'SUCCESS') {
        setSaveState('saved');
        setSavedAt(new Date());
      } else {
        setSaveState('error');
      }
      // Alteração feita enquanto salvava: grava em seguida
      if (pendingRef.current) flush();
    });
  }
  saveRef.current = flush;

  // Ao sair da tela, grava o que ainda estiver pendente
  useEffect(() => {
    const timer = timerRef;
    const save = saveRef;
    return () => {
      clearTimeout(timer.current);
      if (save.current) save.current();
    };
  }, []);

  function commit(next) {
    setFields(next);
    pendingRef.current = next;
    setSaveState('pending');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current(), SAVE_DELAY_MS);
  }

  const marks = fields.marks;
  const activeProduct = products[Math.min(activeProductIndex, products.length - 1)] || products[0];

  function selectProduct(index) {
    setActiveProductIndex(index);
    setNextQuantity(formatQuantity(products[index].defaultQuantity));
  }

  function addMark(mark) {
    const next = [...marks, mark];
    commit({ ...fields, marks: next });
    setSelectedIndex(next.length - 1);
  }

  function handlePoint(point) {
    if (!enabled || !activeProduct) return;
    addMark(createPoint(view, point, activeProduct, toQuantity(nextQuantity, activeProduct.defaultQuantity)));
  }

  function handleStroke(start, end) {
    if (!enabled || !activeProduct) return;
    addMark(createStroke(view, start, end, activeProduct, toQuantity(nextQuantity, activeProduct.defaultQuantity)));
  }

  function handleChange(index, patch) {
    commit({ ...fields, marks: marks.map((mark, i) => (i === index ? { ...mark, ...patch } : mark)) });
  }

  function handleRemove(index) {
    commit({ ...fields, marks: marks.filter((_, i) => i !== index) });
    setSelectedIndex(null);
  }

  function handleField(key, value) {
    commit({ ...fields, [key]: value });
  }

  // Foto do paciente (ou null, de volta ao desenho) como fundo da vista atual
  function handleBackground(next) {
    const backgrounds = { ...fields.backgrounds };
    if (next) backgrounds[view] = next;
    else delete backgrounds[view];
    commit({ ...fields, backgrounds });
  }

  // Clique na lista ou no número: destaca na figura e, editando, abre o popup
  function handleSelect(index) {
    setSelectedIndex(index);
    const mark = visibleMarks[index];
    if (mark && mark.view !== view) setView(mark.view);
    if (enabled) setEditingIndex(index);
  }

  function handleMarkClick(key) {
    const [prefix, index] = key.split('-');
    if (prefix === 'own') handleSelect(Number(index));
  }

  // Arrasto na figura: move o ponto ou o traço inteiro, ou só a ponta da seta
  function handleMarkMove(key, delta, part) {
    const [prefix, rawIndex] = key.split('-');
    if (prefix !== 'own') return;
    const index = Number(rawIndex);
    const mark = marks[index];
    if (!mark) return;
    const clamp = (value) => Math.round(Math.min(1, Math.max(0, value)) * 10000) / 10000;
    const patch = {};
    if (part === 'all') {
      patch.x = clamp(mark.x + delta.dx);
      patch.y = clamp(mark.y + delta.dy);
    }
    if (mark.kind === MARK_KINDS.STROKE && mark.x2 != null) {
      patch.x2 = clamp(mark.x2 + delta.dx);
      patch.y2 = clamp(mark.y2 + delta.dy);
    }
    handleChange(index, patch);
    setSelectedIndex(index);
  }

  function handleMarkHover(key) {
    const [prefix, index] = (key || '').split('-');
    setHoverIndex(prefix === 'own' ? Number(index) : null);
  }

  function handleProductsSaved(list) {
    const next = resolveProducts(list);
    setProducts(next);
    const index = Math.min(activeProductIndex, next.length - 1);
    setActiveProductIndex(index);
    setNextQuantity(formatQuantity(next[index].defaultQuantity));
  }

  const selectedRecord = !enabled
    ? records.find((record) => String(record.id) === selectedRecordId) || records[0] || null
    : null;
  const visibleFields = enabled ? fields : fieldsOf(selectedRecord);
  const visibleMarks = visibleFields.marks;
  const totals = totalsByProduct(visibleMarks);
  const background = (visibleFields.backgrounds || {})[view] || null;

  // Só mapas feitos sobre o mesmo fundo desta vista se sobrepõem com sentido
  const overlayRecords = enabled && showPrevious
    ? previousRecords.filter((record) => sameBackground(backgroundsOf(record)[view], background))
    : [];
  const hasOtherBackground = (record) => (
    !overlayRecords.includes(record) && (record.marks || []).some((mark) => mark.view === view)
  );

  const figureMarks = [
    ...overlayRecords.flatMap((record) =>
      toFigureMarks(record.marks || [], view, { prefix: `prev-${record.id}`, muted: true, title: formatDate(record.markedAt) })),
    ...toFigureMarks(visibleMarks, view, { prefix: 'own', selectedIndex, highlightedIndex: hoverIndex }),
  ];

  const variant = variantFromGender(gender);

  // PDF do mapa exibido: baixa as fotos de fundo antes de montar o template
  const shownRecord = enabled ? ownRecord : selectedRecord;
  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: 'mapa-de-aplicacao',
    prepare: () => buildApplicationMapJob(GetPatientAttachmentFile, {
      markedAt: shownRecord ? shownRecord.markedAt : '',
      marks: visibleMarks,
      backgrounds: visibleFields.backgrounds,
      batch: visibleFields.batch,
      notes: visibleFields.notes,
    }, variant),
  });

  function renderSaveState() {
    if (saveState === 'saved' && savedAt) {
      return <span className={parts.muted}>Salvo às {savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>;
    }
    if (saveState === 'error') return <span className={styles.saveError}>{SAVE_LABELS.error}</span>;
    if (SAVE_LABELS[saveState]) return <span className={parts.muted}>{SAVE_LABELS[saveState]}</span>;
    return null;
  }

  function renderTotals() {
    if (totals.length === 0) return null;
    return (
      <div className={styles.totals}>
        {totals.map((total) => (
          <div key={`${total.product}|${total.unit}`} className={styles.totalRow}>
            <span className={styles.productDot} style={{ background: total.color }} />
            <span className={styles.totalName}>{total.product}</span>
            <span className={styles.totalCount} title={countLabel(total.count)}>{total.count}</span>
            <span className={styles.totalAmount}>{formatAmount(total.total, total.unit)}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <ModuleCard
      accent="purple"
      title="Mapa de aplicação"
      icon={<VaccinesOutlinedIcon />}
      description="Pontos e traços de aplicação com produto e quantidade"
      collapsible={collapsible}
      actions={visibleMarks.length > 0 && (
        <CustomButton
          variant="outlined"
          style="secondary"
          label={report.exporting ? 'Gerando...' : 'PDF'}
          icon={<PictureAsPdfOutlinedIcon />}
          enabled={!report.exporting}
          onClick={report.start}
        />
      )}
    >
      <div className={styles.content}>
        <div className={styles.toolbar}>
          <div className={styles.context}>
            {enabled ? (
              <>
                <span className={parts.muted}>
                  {visibleMarks.length === 0
                    ? 'Clique na figura para marcar um ponto, arraste para um traço'
                    : `${countLabel(visibleMarks.length)} neste atendimento`}
                </span>
                {renderSaveState()}
              </>
            ) : (
              <>
                {selectedRecord && <DateChip label={formatDate(selectedRecord.markedAt)} />}
                {records.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="application-map-record-label">Atendimento</InputLabel>
                    <Select
                      labelId="application-map-record-label"
                      label="Atendimento"
                      value={selectedRecordId || ''}
                      onChange={(event) => { setSelectedRecordId(event.target.value); setSelectedIndex(null); }}
                    >
                      {records.map((record) => (
                        <MenuItem key={record.id} value={String(record.id)}>{formatDate(record.markedAt)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
          </div>

          <div className={styles.controls}>
            {enabled && previousRecords.length > 0 && (
              <FormControlLabel
                control={<Switch checked={showPrevious} onChange={(event) => setShowPrevious(event.target.checked)} size="small" />}
                label="Mostrar anteriores"
                sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
              />
            )}
            <FormControlLabel
              control={<Switch checked={showQuantities} onChange={(event) => setShowQuantities(event.target.checked)} size="small" />}
              label="Exibir quantidades"
              sx={{ marginRight: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            />

            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(event, value) => value && setView(value)}
            >
              {APPLICATION_VIEW_OPTIONS.map((option) => (
                <ToggleButton key={option.key} value={option.key} className={parts.toggleButton}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        </div>

        <div className={styles.layout}>
          <div className={styles.figureArea}>
            {enabled ? (
              <div className={styles.palette} role="radiogroup" aria-label="Produto da próxima marcação">
                {products.map((product, index) => (
                  <button
                    key={`${product.id ?? 'default'}-${index}`}
                    type="button"
                    role="radio"
                    aria-checked={index === activeProductIndex}
                    className={`${styles.productChip} ${index === activeProductIndex ? styles.productChipActive : ''}`}
                    style={{ '--product-color': product.color }}
                    onClick={() => selectProduct(index)}
                  >
                    <span className={styles.productDot} style={{ background: product.color }} />
                    {product.name}
                    <span className={styles.productChipMeta}>{formatAmount(product.defaultQuantity, product.unit)}</span>
                  </button>
                ))}
                <span className={styles.paletteTools}>
                  <TextField
                    size="small"
                    label={activeProduct && activeProduct.unit ? `Qtd. (${activeProduct.unit})` : 'Qtd.'}
                    value={nextQuantity}
                    onChange={(event) => setNextQuantity(event.target.value)}
                    inputProps={{ inputMode: 'decimal', maxLength: 8, style: { width: 56 } }}
                  />
                  <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="Produtos"
                    icon={<TuneOutlinedIcon />}
                    onClick={() => setProductsOpen(true)}
                  />
                </span>
              </div>
            ) : (
              totals.length > 0 && (
                <div className={styles.legend}>
                  {totals.map((total) => (
                    <span key={`${total.product}|${total.unit}`} className={styles.legendItem}>
                      <span className={styles.productDot} style={{ background: total.color }} />
                      {total.product}
                    </span>
                  ))}
                </div>
              )
            )}
            <ApplicationFigure
              view={view}
              variant={variant}
              marks={figureMarks}
              enabled={enabled}
              showQuantities={showQuantities}
              draftColor={activeProduct ? activeProduct.color : undefined}
              onPoint={handlePoint}
              onStroke={handleStroke}
              onMarkClick={handleMarkClick}
              onMarkHover={handleMarkHover}
              onMarkMove={handleMarkMove}
              background={background}
              large
            />
            {enabled && (
              <div className={styles.figureTools}>
                <CustomButton
                  variant="outlined"
                  style="secondary"
                  label={background ? 'Trocar foto' : 'Usar foto do paciente'}
                  icon={<AddPhotoAlternateOutlinedIcon />}
                  onClick={() => setPhotoOpen(true)}
                />
                {background && (
                  <CustomButton
                    variant="outlined"
                    style="secondary"
                    label="Voltar ao desenho"
                    onClick={() => handleBackground(null)}
                  />
                )}
              </div>
            )}
            {enabled && <span className={styles.hint}>Escolha o produto, ajuste a quantidade e clique para marcar um ponto ou arraste para um traço com direção. Arraste a marcação para mover e a ponta da seta para mudar a direção. Clique na marcação para detalhar.</span>}
          </div>

          <div className={styles.panel}>
            {totals.length > 0 && <span className={parts.sectionLabel}>Totais da sessão</span>}
            {renderTotals()}

            <span className={parts.sectionLabel}>Marcações</span>
            <ApplicationMarkList
              marks={visibleMarks}
              view={view}
              selectedIndex={selectedIndex}
              hoverIndex={hoverIndex}
              enabled={enabled}
              onSelect={handleSelect}
              onHover={setHoverIndex}
            />

            {enabled ? (
              <div className={styles.fields}>
                <TextField
                  size="small"
                  fullWidth
                  label="Lote dos produtos"
                  value={fields.batch}
                  inputProps={{ maxLength: 120 }}
                  onChange={(event) => handleField('batch', event.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Observações da sessão"
                  value={fields.notes}
                  inputProps={{ maxLength: 2000 }}
                  onChange={(event) => handleField('notes', event.target.value)}
                />
              </div>
            ) : (
              (visibleFields.batch || visibleFields.notes) && (
                <div className={styles.fields}>
                  {visibleFields.batch && <span className={styles.fieldText}>Lote: {visibleFields.batch}</span>}
                  {visibleFields.notes && <span className={styles.fieldText}>{visibleFields.notes}</span>}
                </div>
              )
            )}

            {enabled && showPrevious && (
              <div className={styles.previous}>
                <span className={parts.sectionLabel}>Atendimentos anteriores</span>
                {previousRecords.map((record) => (
                  <div key={record.id} className={styles.previousRecord}>
                    <div className={styles.previousHead}>
                      <DateChip label={formatDate(record.markedAt)} compact />
                      <span className={parts.muted}>{countLabel((record.marks || []).length)}</span>
                      {hasOtherBackground(record) && <span className={styles.tag} title="Marcado sobre outro fundo nesta vista; não aparece na figura">Outro fundo</span>}
                    </div>
                    {totalsByProduct(record.marks || []).map((total) => (
                      <span key={`${total.product}|${total.unit}`} className={styles.previousLine}>
                        {total.product} · {formatAmount(total.total, total.unit)} · {countLabel(total.count)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {report.job && (
        <HiddenReport>
          <ApplicationMapReportTemplate ref={report.ref} {...report.paperProps} {...report.job} />
        </HiddenReport>
      )}

      {editingIndex !== null && marks[editingIndex] && (
        <ApplicationMarkPopup
          mark={marks[editingIndex]}
          number={editingIndex + 1}
          products={products}
          onSave={(patch) => handleChange(editingIndex, patch)}
          onRemove={() => handleRemove(editingIndex)}
          OnHide={() => setEditingIndex(null)}
        />
      )}

      {productsOpen && (
        <ApplicationProductsPopup
          products={products}
          onSaved={handleProductsSaved}
          OnHide={() => setProductsOpen(false)}
        />
      )}

      {photoOpen && (
        <ApplicationPhotoPopup
          patientId={patientId}
          appointmentId={appointmentId}
          view={view}
          currentDate={currentDate}
          background={background}
          onSelect={handleBackground}
          OnHide={() => setPhotoOpen(false)}
        />
      )}
    </ModuleCard>
  );
};

export default ApplicationMap;

// Resumo usado por quem lista marcações fora do card
export { markSummary };
